// A dark code window with tabs (Console D, the mock's settings code(): Install, Upload, Agents):
// one tab per snippet, an optional note above it, and Copy for the selected tab's command. Tabs
// follow the WAI-ARIA pattern (arrow keys, Home, End); Copy sits beside the tablist, not in it.
// Code and commands are never translated: callers mark comments, strings and keywords with
// <span className="cm|s|k">.
import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react'
import { Icon } from '../icons'
import { useUiText } from '../text'
import { copyText, useToast } from './Toast'

export interface CodeTab {
  id: string
  /** The tab's name: a platform or a tool, never translated. */
  label: string
  note?: ReactNode
  code: ReactNode
  /** What Copy puts on the clipboard for this tab. */
  copy: string
}

export function CodeTabs({
  label,
  tabs,
  wrap = false,
  notesAbove = false,
}: {
  label: string
  tabs: readonly CodeTab[]
  /** Wrap long lines instead of scrolling them (APS-379): for prose-like text, never commands. */
  wrap?: boolean
  /**
   * The selected tab's note as a hint above the window, which then holds commands and config
   * only (APS-380): prose never reads as a wrapped paragraph inside a code box.
   */
  notesAbove?: boolean
}) {
  const words = useUiText()
  const base = useId()
  const [selected, setSelected] = useState(tabs[0]?.id)
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const { toast, show } = useToast()
  const current = tabs.find((x) => x.id === selected) ?? tabs[0]
  if (!current) return null

  function onKey(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    const last = tabs.length - 1
    const next =
      e.key === 'ArrowRight'
        ? i === last
          ? 0
          : i + 1
        : e.key === 'ArrowLeft'
          ? i === 0
            ? last
            : i - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : -1
    if (next < 0) return
    e.preventDefault()
    setSelected(tabs[next]?.id)
    buttons.current[next]?.focus()
  }

  return (
    <>
      {notesAbove && current.note ? (
        <p className="code-hint" id={`${base}-hint`}>
          {current.note}
        </p>
      ) : null}
      <div className={wrap ? 'code code-d wrap' : 'code code-d'}>
        <div className="code-tabs">
          <div role="tablist" aria-label={label}>
            {tabs.map((tab, i) => (
              <button
                key={tab.id}
                ref={(el) => {
                  buttons.current[i] = el
                }}
                type="button"
                role="tab"
                id={`${base}-t-${tab.id}`}
                aria-controls={`${base}-p-${tab.id}`}
                aria-selected={tab.id === current.id}
                tabIndex={tab.id === current.id ? 0 : -1}
                translate="no"
                onClick={() => setSelected(tab.id)}
                onKeyDown={(e) => onKey(e, i)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="cp btn ghost sm"
            onClick={async () =>
              show((await copyText(current.copy)) ? words.copied : words.copyFailed)
            }
          >
            <Icon name="copy" />
            {words.copy}
          </button>
        </div>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`${base}-p-${tab.id}`}
            aria-labelledby={`${base}-t-${tab.id}`}
            aria-describedby={notesAbove && tab.note ? `${base}-hint` : undefined}
            hidden={tab.id !== current.id}
          >
            {tab.note && !notesAbove ? <p className="se-code-note">{tab.note}</p> : null}
            {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable code block must be reachable by keyboard (WCAG 2.1.1) */}
            <pre translate="no" tabIndex={0}>
              {tab.code}
            </pre>
          </div>
        ))}
        {toast}
      </div>
    </>
  )
}
